import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import type { SupportedLocale } from '@brightdrop/localization-core';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '~/app/store';
import useStoredLocale from '~/common/hooks/useStoredLocale';
import { LOCALIZATION_FEATURE_FLAG } from '~/common/models/featureFlags.model';
import { getStoredLocale } from '~/common/utils/signed-out-locale.utils';
import { setCurrentLocale } from '~/store/profile/profileSlice';
import {
  selectCurrentLocale,
  selectProfile,
} from '~/store/profile/profileSlice.selectors';

type QuickPayLocale = {
  /** True when a signed-out visitor should see the locale selector. */
  showSelector: boolean;
  locale: SupportedLocale;
  onLocaleChange: (locale: SupportedLocale) => void;
};

/**
 * Lets a signed-out visitor choose the language Quick Pay speaks.
 *
 * The choice is shared with the login screen through local storage. While
 * Quick Pay is mounted it is applied to the app language; on leaving, the
 * language the visitor arrived with is restored, so the login screen keeps its
 * own behavior. A signed-in user keeps the profile locale and gets no selector.
 * @returns Whether to show the selector, the selected locale, and its change handler
 */
const useQuickPayLocale = (): QuickPayLocale => {
  // TODO: A visitor's language is written to the profile slice's
  // `currentLocale` only because that is what the app syncs to i18n and the
  // request headers. Move it to the localization core as the single source of
  // the active locale.
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const { getFlag } = useFeatureFlags();
  const hasProfile = Boolean(useSelector(selectProfile));
  const currentLocale = useSelector(selectCurrentLocale);
  // The sign-in session is known before the profile request resolves, so a
  // signed-in user is not mistaken for a visitor while the profile loads.
  // `useIsAuthenticated` is false while MSAL starts up, so visitor behavior
  // waits until the cached account has been read.
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const isSignedIn = isAuthenticated || hasProfile;
  const isMsalReady = inProgress !== InteractionStatus.Startup;
  const isEnabled =
    Boolean(getFlag(LOCALIZATION_FEATURE_FLAG)) && isMsalReady && !isSignedIn;

  const [locale, setStoredLocale] = useStoredLocale();

  // Read inside the effect and its cleanup, when the profile may have loaded
  // since the effect ran.
  const isSignedInRef = useRef(isSignedIn);
  isSignedInRef.current = isSignedIn;
  const i18nRef = useRef(i18n);
  i18nRef.current = i18n;

  useEffect(() => {
    if (!isEnabled) return;

    const arrivedWith = i18nRef.current.language;

    return () => {
      if (!isSignedInRef.current && arrivedWith) {
        dispatch(setCurrentLocale(arrivedWith));
      }
    };
  }, [dispatch, isEnabled]);

  // Applies the stored locale on arrival and again if something else sets the
  // app locale afterwards: on a direct load the app shell's own effect runs
  // after this one and writes the browser language over it.
  useEffect(() => {
    if (!isEnabled) return;

    const stored = getStoredLocale();
    if (stored && stored !== currentLocale) {
      dispatch(setCurrentLocale(stored));
    }
  }, [currentLocale, dispatch, isEnabled]);

  const onLocaleChange = useCallback(
    (next: SupportedLocale) => {
      setStoredLocale(next);
      dispatch(setCurrentLocale(next));
    },
    [dispatch, setStoredLocale]
  );

  return { showSelector: isEnabled, locale, onLocaleChange };
};

export default useQuickPayLocale;
